import { ReactNode } from 'react';

import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { ValidatedPrescriptionPackage } from '~libs.shared.prescription';

import { sentPrescriptionPackagesTranslationKeys } from '../../../../translations';
import { PrescriptionListHeader } from '../prescription/PrescriptionListHeader/PrescriptionListHeader';

import { SentMagistralDrugPrescriptionItem } from './magistralDrugPrescription/SentMagistralDrugPrescriptionItem';
import { PrescriptionPackageAccessCode, SentPrescriptionsList } from './SentPrescriptionPackagesList.types';
import { SentStandardPrescriptionItem } from './standardPrescription/SentStandardPrescriptionItem';
import { mapPrescriptionPackageToSentPrescriptionPackage } from './SentPrescriptionPackagesList.utils';

export const renderPrescriptionsList = (
   prescriptions: SentPrescriptionsList,
   visitDiagnoses: VisitDiagnosis[],
   visitId?: number
): JSX.Element[] =>
   prescriptions.map((prescription) => {
      const { type } = prescription;

      switch (type) {
         case 'standard': {
            return (
               <SentStandardPrescriptionItem
                  key={prescription.id}
                  testId={prescription.id}
                  visitId={visitId}
                  visitDiagnoses={visitDiagnoses}
                  {...prescription}
               />
            );
         }
         case 'magistralDrug': {
            return (
               <SentMagistralDrugPrescriptionItem
                  key={prescription.id}
                  testId={prescription.id}
                  visitId={visitId}
                  {...prescription}
               />
            );
         }
         default:
            return assertUnreachable(type);
      }
   });

type PackageViewRendererProps = {
   id: number;
   packageNumber: number;
   accessCode: PrescriptionPackageAccessCode | undefined;
   renderPrescriptions: () => JSX.Element[];
};

type PackageViewRenderer = (renderProps: PackageViewRendererProps) => ReactNode;

type SentPrescriptionPackagesListProps = {
   readonly sentPrescriptionPackages: ValidatedPrescriptionPackage[];
   readonly packageViewRenderer: PackageViewRenderer;
   readonly visitId: number;
   readonly visitDiagnoses: VisitDiagnosis[];
};

export const SentPrescriptionPackagesList = (props: SentPrescriptionPackagesListProps): JSX.Element => {
   const { sentPrescriptionPackages, packageViewRenderer } = props;

   return (
      <StyledFlex direction={'column'} gap={'4px'}>
         <PrescriptionListHeader
            testId={'sendPrescriptionPackages'}
            label={sentPrescriptionPackagesTranslationKeys.issuedPrescriptions}
         />
         <StyledFlex direction={'column'} gap={'14px'}>
            {sentPrescriptionPackages
               .map(mapPrescriptionPackageToSentPrescriptionPackage)
               .map(({ id, accessCode, prescriptions }, index) =>
                  packageViewRenderer({
                     id: id,
                     packageNumber: sentPrescriptionPackages.length - index,
                     accessCode: accessCode,
                     renderPrescriptions: () =>
                        renderPrescriptionsList(prescriptions, props.visitDiagnoses, props.visitId),
                  })
               )}
         </StyledFlex>
      </StyledFlex>
   );
};
